import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEye,
  FiFileText,
  FiSearch,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

const ROWS_PER_PAGE = 10;

function numberValue(value) {
  return Number(value || 0);
}

function formatMoney(value) {
  return `₹${numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function advanceRecoveryAmount(payslip) {
  return (payslip.adjustments || [])
    .filter(
      (adjustment) =>
        adjustment.type === "DEDUCTION" &&
        String(adjustment.description || "").startsWith("EMPLOYEE_ADVANCE:"),
    )
    .reduce((sum, adjustment) => sum + numberValue(adjustment.amount), 0);
}

function formatMonth(year, month) {
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function statusLabel(status) {
  const labels = {
    DRAFT: "Draft",
    POSTED: "Ready to Pay",
    PAID: "Paid",
    VOIDED: "Voided",
  };

  return labels[status] || status;
}

export default function Payslips() {
  const { accessToken } = useAuth();

  const [payslips, setPayslips] = useState([]);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [hrmsFilters, setHrmsFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadPayslips = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest("/payroll/payslips", {
        accessToken,
      });

      setPayslips(response || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load payslips.");
      setPayslips([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadPayslips();
  }, [loadPayslips]);

  const monthOptions = useMemo(() => {
    const uniqueMonths = new Map();

    payslips.forEach((payslip) => {
      const run = payslip.payrollRun;
      const value = `${run.periodYear}-${String(run.periodMonth).padStart(
        2,
        "0",
      )}`;

      uniqueMonths.set(value, formatMonth(run.periodYear, run.periodMonth));
    });

    return Array.from(uniqueMonths, ([value, label]) => ({
      value,
      label,
    }));
  }, [payslips]);

  const filteredPayslips = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return payslips.filter((payslip) => {
      const matchesSearch =
        !normalizedSearch ||
        payslip.employeeNameSnapshot
          ?.toLowerCase()
          .includes(normalizedSearch) ||
        payslip.employeeCodeSnapshot?.toLowerCase().includes(normalizedSearch);

      const payrollMonth = `${payslip.payrollRun.periodYear}-${String(
        payslip.payrollRun.periodMonth,
      ).padStart(2, "0")}`;

      const matchesMonth =
        monthFilter === "All" || payrollMonth === monthFilter;

      const matchesStatus =
        statusFilter === "All" || payslip.status === statusFilter;

      return matchesHrmsFilters(payslip,hrmsFilters) && matchesSearch && matchesMonth && matchesStatus;
    });
  }, [payslips, search, monthFilter, statusFilter, hrmsFilters]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPayslips.length / ROWS_PER_PAGE),
  );

  const paginatedPayslips = filteredPayslips.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const summary = useMemo(
    () => ({
      total: payslips.length,
      paid: payslips.filter((item) => item.status === "PAID").length,
      draft: payslips.filter((item) => item.status === "DRAFT").length,
      readyToPay: payslips.filter((item) => item.status === "POSTED").length,
    }),
    [payslips],
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Payslips</h1>

        <p className="mt-2 text-gray-500">
          Review payroll amounts and track employee payment status.
        </p>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Total Payslips"
          value={summary.total}
          icon={<FiFileText size={22} />}
          iconBg="bg-primary-50"
          iconColor="text-primary-700"
        />

        <SummaryCard
          title="Paid"
          value={summary.paid}
          icon={<FiCheckCircle size={22} />}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />

        <SummaryCard
          title="Draft"
          value={summary.draft}
          icon={<FiClock size={22} />}
          iconBg="bg-yellow-50"
          iconColor="text-yellow-600"
        />

        <SummaryCard
          title="Ready To Pay"
          value={summary.readyToPay}
          icon={<FiCheckCircle size={22} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
      </div>

      <HrmsFilters records={payslips} value={hrmsFilters} onChange={next => { setHrmsFilters(next); setCurrentPage(1); }} />
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              type="text"
              placeholder="Search employee..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-2xl border border-gray-200 py-3 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <select
            value={monthFilter}
            onChange={(event) => {
              setMonthFilter(event.target.value);
              setCurrentPage(1);
            }}
            className="rounded-2xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="All">All Months</option>

            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setCurrentPage(1);
            }}
            className="rounded-2xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="All">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="POSTED">Ready To Pay</option>
            <option value="PAID">Paid</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <TableHead>Employee</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Month</TableHead>
                <TableHead align="right">Gross Earnings</TableHead>
                <TableHead align="right">Deductions</TableHead>
                <TableHead align="right">Before Recovery</TableHead>
                <TableHead align="right">Advance Recovery</TableHead>
                <TableHead align="right">Final Payable</TableHead>
                <TableHead>Status</TableHead>
                <TableHead align="center">Action</TableHead>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="py-16 text-center text-gray-500">
                    Loading payslips...
                  </td>
                </tr>
              ) : paginatedPayslips.length ? (
                paginatedPayslips.map((payslip) => (
                  <tr
                    key={payslip.id}
                    className="transition-all hover:bg-gray-50"
                  >
                    <td className="px-6 py-5">
                      <p className="font-semibold text-gray-900">
                        {payslip.employeeNameSnapshot}
                      </p>
                    </td>

                    <td className="px-6 py-5 text-gray-600">
                      {payslip.employeeCodeSnapshot}
                    </td>

                    <td className="px-6 py-5 text-gray-600">
                      {formatMonth(
                        payslip.payrollRun.periodYear,
                        payslip.payrollRun.periodMonth,
                      )}
                    </td>

                    <AmountCell value={payslip.grossEarnings} />
                    <AmountCell value={payslip.totalDeductions} />
                    <AmountCell
                      value={
                        numberValue(payslip.netPayable) +
                        advanceRecoveryAmount(payslip)
                      }
                    />
                    <AmountCell
                      value={advanceRecoveryAmount(payslip)}
                      deduction
                    />
                    <AmountCell value={payslip.netPayable} strong />

                    <td className="px-6 py-5">
                      <StatusBadge status={payslip.status} />
                    </td>

                    <td className="px-6 py-5">
                      <div className="flex justify-center">
                        <Link
                          to={`/admindashboard/payroll/payslips/${payslip.id}`}
                          className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700 transition-all hover:bg-primary-100"
                          title="View payslip"
                        >
                          <FiEye size={18} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="py-16 text-center">
                    <FiFileText size={40} className="mx-auto text-gray-300" />
                    <p className="mt-4 text-gray-500">No payslips found.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-4 border-t border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-gray-500">
            Showing{" "}
            {filteredPayslips.length
              ? (currentPage - 1) * ROWS_PER_PAGE + 1
              : 0}
            –{Math.min(currentPage * ROWS_PER_PAGE, filteredPayslips.length)} of{" "}
            {filteredPayslips.length} payslips
          </p>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage((previous) => Math.max(previous - 1, 1))
              }
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              <FiChevronLeft />
            </button>

            <span className="rounded-xl bg-primary-50 px-4 py-2 font-medium text-primary-700">
              {currentPage}
            </span>

            <button
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((previous) => Math.min(previous + 1, totalPages))
              }
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, icon, iconBg, iconColor }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-3 text-3xl font-bold text-gray-900">{value}</h3>
        </div>

        <div
          className={`flex h-14 w-14 items-center justify-center rounded-2xl ${iconBg} ${iconColor}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function AmountCell({ value, strong = false, deduction = false }) {
  return (
    <td
      className={`px-6 py-5 text-right ${
        strong
          ? "font-bold text-primary-700"
          : deduction
            ? "font-semibold text-red-600"
            : "font-semibold text-gray-900"
      }`}
    >
      {deduction && numberValue(value) > 0 ? "−" : ""}
      {formatMoney(value)}
    </td>
  );
}

function TableHead({ children, align = "left" }) {
  return (
    <th
      className={`px-6 py-4 text-${align} text-xs font-semibold uppercase tracking-wider text-gray-500`}
    >
      {children}
    </th>
  );
}

function StatusBadge({ status }) {
  const styles = {
    DRAFT: "border-yellow-100 bg-yellow-50 text-yellow-700",
    POSTED: "border-blue-100 bg-blue-50 text-blue-700",
    PAID: "border-green-100 bg-green-50 text-green-700",
    VOIDED: "border-red-100 bg-red-50 text-red-700",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold ${
        styles[status] || "border-gray-100 bg-gray-50 text-gray-700"
      }`}
    >
      {statusLabel(status)}
    </span>
  );
}
